import React, { useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { Field, FieldProps, Formik, FormikProps, FormikValues } from 'formik';

import { Dialog } from '@components/shared/Dialog/Dialog';
import { useT } from '@features/oneOnboarding/hooks/useT';
import {
  mutateBranch,
  mutateClient,
  mutateJobType,
  mutatePurchaseOrder,
  mutateRequisition,
  mutateSite,
} from '@hooks/apiHooks';
import { useTenant } from '@hooks/useComponentMapping';
import { BranchModel } from '@models/branch.model';
import { ClientModel } from '@models/client.model';
import { PurchaseOrderModel } from '@models/purchaseOrder.model';
import { RegionModel } from '@models/region.model';
import { JobTypeModel, RequisitionModel } from '@models/requisition.model';
import { SiteModel } from '@models/site.model';
import { Autocomplete, TextField } from '@mui/material';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQueryClient } from '@tanstack/react-query';

import { formItems, initialValues, parseNestValidationErrors, validationSchema } from '../../masterDataHelpers';
type CreateUpdateData<T> = {
  activeTab: keyof typeof validationSchema;
  dataSources: {
    branches?: BranchModel[];
    sites?: SiteModel[];
    clients: ClientModel[];
    requisitions?: RequisitionModel[];
    purchaseOrders?: PurchaseOrderModel[];
    jobTypes?: JobTypeModel[];
    regions?: RegionModel[];
  };
  selectedRow: T | null;
  setSelectedRow: (item: T | null) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
};

const mutateData = {
  branch: mutateBranch,
  site: mutateSite,
  requisition: mutateRequisition,
  purchaseOrder: mutatePurchaseOrder,
  client: mutateClient,
  jobType: mutateJobType,
};

function CreateUpdateData<T>({
  activeTab,
  dataSources,
  selectedRow,
  setSelectedRow,
  modalOpen,
  setModalOpen,
}: CreateUpdateData<T>) {
  const { t, i18n } = useT('candidate_recruiter', 'entry');
  const tenantId = useTenant();
  const formikRef = useRef<FormikProps<T> | null>(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { mutateAsync } = mutateData[activeTab](queryClient, !!selectedRow, true, (data: any) => {
    let msg;
    if (!data.response?.data?.error && data?.id) {
      dispatch(
        snackbarSlice.actions.showSuccess({
          message: `${activeTab} was ${selectedRow ? 'updated' : 'created'} successfully `,
        }),
      );
      setModalOpen(false);
      setSelectedRow(null);
    }
    if (data?.response?.data?.error?.data?.validationErrors) {
      const { message } = data.response.data.error.data;
      if (message) {
        msg = message;
      }
      const fieldErrors = parseNestValidationErrors(data.response.data.error.data.validationErrors);

      dispatch(
        snackbarSlice.actions.showError({
          message: msg || JSON.stringify(fieldErrors) || `Server Error`,
        }),
      );
    } else if (data.response?.data?.error) {
      dispatch(
        snackbarSlice.actions.showError({
          message: `Server Error`,
        }),
      );
    }
  });

  const selectOption: {
    siteId: Partial<SiteModel>[];
    branchName: Partial<BranchModel>[];
    clientId: Partial<ClientModel>[];
    regionId: Partial<RegionModel>[];
    jobTypeId: Partial<JobTypeModel>[];
    [key: string]: any;
  } = useMemo(() => {
    const sitesOptions = (dataSources.sites ?? []).map((site, idx) => ({
      id: site.id,
      name: `${site.name} | ${site.code} | ${idx}`,
    }));

    const regionsOptions = (dataSources.regions ?? []).map((region) => ({ id: region.id, name: region.name }));

    const branchOptions = (dataSources.branches ?? []).map((branch) => ({ id: branch.id, name: branch.name }));

    const clientOptions = (dataSources.clients ?? []).map((client) => ({
      id: client.id,
      name: `${client.name} |  ${client.customerId}`,
    }));
    const jobTypeOptions = (dataSources.jobTypes ?? []).map((job) => ({
      id: job.id,
      name: `${job.name}`,
    }));
    return {
      branchName: branchOptions,
      siteId: sitesOptions,
      jobTypeId: jobTypeOptions,
      regionId: regionsOptions,
      clientId: clientOptions,
    };
  }, [dataSources]);
  const textErrors = (meta: any) => ({
    error: meta.touched && Boolean(meta.error),
    helperText: meta.touched && meta.error,
  });

  const translate = (name: string, label: string) => {
    const key = `recruiter:GENERAL.CONFIGURATION.${name}`;
    return i18n.exists(key) ? t(key) : label;
  };

  const handleSave = () => {
    if (formikRef.current) {
      formikRef?.current?.submitForm();
    }
  };

  const handleSubmit = async (data: any) => {
    data.tenantId = tenantId;
    // check if null remove delete from object
    Object.keys(data).forEach((key) => {
      if (data[key] === '' || data[key] === null) {
        delete data[key];
      }
    });

    if (data?.customData) {
      try {
        data.customData = JSON.parse(data?.customData);
      } catch (err) {
        window.alert('Invalid JSON for customData field, try pasting directly the json you want to introduce');
        return;
      }
    }
    if (window.confirm('Are you sure you want to save changes?')) {
      await mutateAsync({ data, id: data?.id || data?.requisitionId });
    }
  };
  const renderFormElements = (component: string, name: string, label: string, useName?: boolean) => {
    switch (component) {
      case 'TextField':
        return (
          <Field name={name} key={name}>
            {(fieldProps: FieldProps<any, FormikValues>) => {
              const { error, helperText } = textErrors(fieldProps.meta);
              return (
                <div className={`tag-ds input-wrapper input-size mt2 ${error ? 'input-wrapper_error' : ''}`}>
                  <label className="">{translate(name, label)}</label>
                  <div className="input-container">
                    <input
                      type="text"
                      value={fieldProps.field.value}
                      placeholder=""
                      onChange={fieldProps.field.onChange}
                      onBlur={fieldProps.field.onBlur}
                      name={fieldProps.field.name}
                    />
                  </div>
                  {helperText && <p className="input-helper-text">{helperText}</p>}
                </div>
              );
            }}
          </Field>
        );
      case 'Select':
        return (
          <Field name={name} key={name}>
            {(fieldProps: FieldProps<any, FormikValues>) => {
              const { error, helperText } = textErrors(fieldProps.meta);
              const options = selectOption[name] || [];
              const selectedOption =
                options.find((opt: any) => opt.id === fieldProps.field.value) ||
                options.find((opt: any) => opt.name === fieldProps.field.value) ||
                null;
              return (
                <Autocomplete
                  options={options}
                  getOptionLabel={(option) => option?.name || ''}
                  value={selectedOption}
                  onChange={(_, newValue) => {
                    fieldProps.form.setFieldValue(fieldProps.field.name, useName ? newValue?.name : newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_, inputValue, reason) => {
                    if (reason === 'input') {
                      fieldProps.form.setFieldValue(fieldProps.field.name, inputValue);
                    }
                  }}
                  renderInput={(params) => (
                    <div className={`tag-ds input-wrapper input-size mt2 ${error ? 'input-wrapper_error' : ''}`}>
                      <label className="">{translate(fieldProps.field.name, label)}</label>
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        error={Boolean(error)}
                        helperText={helperText}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: null,
                        }}
                      />
                      {helperText && <p className="input-helper-text">{helperText}</p>}
                    </div>
                  )}
                />
              );
            }}
          </Field>
        );
      case 'Checkbox':
        return (
          <Field name={name} key={name}>
            {(fieldProps: FieldProps<any, FormikValues>) => {
              return (
                <div className="tag-ds checkbox-container  ">
                  <input
                    type="checkbox"
                    checked={fieldProps.field.value}
                    onChange={fieldProps.field.onChange}
                    onBlur={fieldProps.field.onBlur}
                    name={fieldProps.field.name}
                  />
                  <label htmlFor="checkbox">{translate(name, label)}</label>
                </div>
              );
            }}
          </Field>
        );
      case 'JsonEditor':
        return (
          <Field name={name} key={name}>
            {(fieldProps: FieldProps<any, FormikValues>) => {
              const { error, helperText } = textErrors(fieldProps.meta);

              const value =
                typeof fieldProps.field.value === 'string'
                  ? fieldProps.field.value
                  : JSON.stringify(fieldProps.field.value || {}, null, 2);

              return (
                <div className={`tag-ds input-wrapper input-size mt2 ${error ? 'input-wrapper_error' : ''}`}>
                  <label>{translate(name, label)}</label>

                  <textarea
                    value={value}
                    name={fieldProps.field.name}
                    onChange={(e) => {
                      fieldProps.form.setFieldValue(name, e.target.value);
                    }}
                    onBlur={fieldProps.field.onBlur}
                    style={{
                      width: '100%',
                      minHeight: 150,
                      fontFamily: 'monospace',
                      padding: 10,
                      border: '1px solid #ccc',
                      borderRadius: 4,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        const formatted = JSON.stringify(JSON.parse(value), null, 2);
                        fieldProps.form.setFieldValue(name, formatted);
                      } catch (err) {
                        fieldProps.form.setFieldError(name, err.message);
                      }
                    }}
                    style={{ marginTop: 6 }}
                  >
                    Format JSON
                  </button>

                  {helperText && <p className="input-helper-text">{helperText}</p>}
                </div>
              );
            }}
          </Field>
        );
    }
  };
  return (
    <>
      {modalOpen && (
        <div className="tag-ds">
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            fullWidth
            confirmLabel={t('candidate_recruiter:GENERAL.GENERIC.save')}
            onConfirm={handleSave}
          >
            <Formik
              innerRef={formikRef as any}
              initialValues={selectedRow || initialValues[activeTab]}
              onSubmit={handleSubmit}
              validationSchema={validationSchema[activeTab]}
              enableReinitialize={true}
            >
              {(props: FormikProps<any>) => (
                <form onSubmit={props.handleSubmit}>
                  <div className="tag-ds">
                    <div className="head-container">
                      <h3 className="head-title">
                        {translate(`${activeTab}SettingsTitle`, `Enter ${activeTab} Details`)}
                      </h3>
                      <p>{translate(`${activeTab}SettingsSubTitle`, 'Please provide the correct information')}</p>
                    </div>
                    {formItems[activeTab].map(({ component, name, label, useName }) =>
                      renderFormElements(component, name, label, useName),
                    )}
                  </div>
                </form>
              )}
            </Formik>
          </Dialog>
        </div>
      )}
    </>
  );
}

export default CreateUpdateData;
